import { useEffect, useState } from "react";
import { CalendarDays, Lock, CalendarClock } from "lucide-react";
import { EmployeeLayout } from "@/components/EmployeeLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, apiErrorCode } from "@/lib/api";
import { EMPLOYEE_VACATION_REQUEST, EMPLOYEE_PORTAL } from "@/constants/testIds";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending: "Pendiente",
  awaiting_confirmation: "Por confirmar",
  approved: "Aprobada",
  rejected: "Rechazada",
  declined: "Rechazada",
};

function isWorkday(weekday, workWeek) {
  if (workWeek === "mon_fri") return weekday < 5;
  if (workWeek === "mon_sat") return weekday < 6;
  return true;
}

function previewDays(start, end, workWeek) {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (endDate < startDate) return null;
  let days = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    if (isWorkday(current.getDay() === 0 ? 6 : current.getDay() - 1, workWeek)) days += 1;
    current.setDate(current.getDate() + 1);
  }
  return days;
}

export default function EmployeeVacation() {
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [requests, setRequests] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "" });

  const load = () => {
    if (!user?.employee_id) return;
    api.get(`/employees/${user.employee_id}`).then(({ data }) => setEmployee(data));
    api.get(`/employees/${user.employee_id}/vacation-requests`).then(({ data }) => setRequests(data));
  };

  useEffect(load, [user]);

  const handleRequest = async (e) => {
    e.preventDefault();
    setBlocked(null);
    try {
      const { data } = await api.post(`/employees/${user.employee_id}/vacation-requests`, form);
      toast.success(`${data.message} (${data.days_requested} días)`);
      setOpen(false);
      setForm({ start_date: "", end_date: "" });
      load();
    } catch (err) {
      if (apiErrorCode(err) === "POLICIES_BLOCKING") {
        setBlocked(err.response.data.detail.message);
        setOpen(false);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  };

  const confirmRequest = async (id) => {
    try {
      const { data } = await api.post(`/vacation-requests/${id}/confirm`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const declineRequest = async (id) => {
    try {
      const { data } = await api.post(`/vacation-requests/${id}/decline`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!employee) return <EmployeeLayout><p className="text-charcoal/60">Cargando...</p></EmployeeLayout>;

  const preview = previewDays(form.start_date, form.end_date, user?.work_week || "mon_fri");
  const awaitingConfirmation = requests.filter((r) => r.status === "awaiting_confirmation");
  const currentYear = new Date().getFullYear();
  const vacationPeriods = Array.isArray(employee.vacation_periods) ? employee.vacation_periods : [];
  const unscheduledDays = vacationPeriods.filter((v) => v.year < currentYear).reduce((sum, v) => sum + (v.days_available ?? 0), 0);

  return (
    <EmployeeLayout>
      <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Mis vacaciones</h1>
      <p className="text-sm text-charcoal/60 mb-5">Conforme a los Art. 76 y 78 de la LFT.</p>

      {unscheduledDays > 0 && (
        <div data-testid={EMPLOYEE_PORTAL.unscheduledVacationBanner} className="bg-accent border border-petroleum/20 rounded-2xl p-4 flex items-start gap-3 mb-4">
          <CalendarClock className="w-4 h-4 text-petroleum mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="text-sm text-charcoal/80">Tienes <b>{unscheduledDays}</b> día(s) de años anteriores sin programar tomarlos. Coordina con tu empresa cuándo disfrutarlos.</p>
            <p className="text-xs text-charcoal/50 mt-1">
              Estos días siguen en tu saldo mientras la empresa no indique lo contrario, y no se pueden cambiar por dinero mientras sigas activo (Art. 79 LFT prohíbe pagar las vacaciones en lugar de tomarlas). Aun así, coordina pronto para tomarlos: la ley espera que se otorguen dentro de los 6 meses siguientes al aniversario laboral, y entre más tiempo pase sin programarlos, más dudoso es su tratamiento si algún día se disputan (Art. 76 a 81 y 516 LFT).
            </p>
          </div>
        </div>
      )}

      {vacationPeriods.map((v) => (
        <div key={v.year} className="bg-card border border-petroleum/10 rounded-2xl p-5 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
            <p className="font-semibold text-charcoal">{v.year}</p>
            {v.year < currentYear && v.days_available > 0 && (
              <span className="text-xs font-medium text-petroleum bg-petroleum/10 px-2 py-0.5 rounded-full">Sin programar</span>
            )}
          </div>
          <p className="text-3xl font-heading font-bold text-petroleum" data-testid="employee-vacation-balance">{v.days_available}</p>
          <p className="text-xs text-charcoal/50">días disponibles de {v.days_earned} otorgados ({v.days_taken} tomados)</p>
        </div>
      ))}
      {employee.vacation_periods.length === 0 && <p className="text-sm text-charcoal/50">Sin periodos registrados aún.</p>}

      {awaitingConfirmation.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-semibold text-charcoal mb-2">Vacaciones asignadas por tu empresa</p>
          {awaitingConfirmation.map((r) => (
            <div key={r.id} data-testid="vacation-awaiting-confirmation-card" className="bg-accent border border-terracotta/30 rounded-2xl p-4 mb-2">
              <p className="text-sm text-charcoal">{r.start_date} → {r.end_date} ({r.days_requested} días)</p>
              <div className="flex gap-2 mt-3">
                <Button data-testid={`confirm-vacation-${r.id}`} size="sm" onClick={() => confirmRequest(r.id)} className="bg-petroleum rounded-full flex-1">Confirmar</Button>
                <Button data-testid={`decline-vacation-${r.id}`} size="sm" onClick={() => declineRequest(r.id)} variant="outline" className="rounded-full flex-1">Rechazar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {blocked && (
        <div data-testid={EMPLOYEE_VACATION_REQUEST.blockedBanner} className="bg-accent border border-terracotta/30 rounded-2xl p-5 flex items-start gap-3 mt-4">
          <Lock className="w-5 h-5 text-terracotta shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm text-charcoal/80">{blocked}</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid={EMPLOYEE_VACATION_REQUEST.requestButton} className="w-full bg-terracotta rounded-full mt-4">Solicitar vacaciones</Button>
        </DialogTrigger>
        <DialogContent className="bg-bone">
          <DialogHeader><DialogTitle>Solicitar vacaciones</DialogTitle></DialogHeader>
          <form onSubmit={handleRequest} className="space-y-3">
            <div>
              <Label>Fecha de inicio</Label>
              <Input data-testid={EMPLOYEE_VACATION_REQUEST.startDateInput} type="date" required value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <Label>Fecha de fin</Label>
              <Input data-testid={EMPLOYEE_VACATION_REQUEST.endDateInput} type="date" required value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
            </div>
            {preview !== null && (
              <p data-testid="vacation-request-days-preview" className="text-sm text-charcoal/70">Días laborables estimados: <b>{preview}</b></p>
            )}
            <Button data-testid={EMPLOYEE_VACATION_REQUEST.submitButton} type="submit" className="w-full bg-petroleum rounded-full">Enviar solicitud</Button>
          </form>
        </DialogContent>
      </Dialog>

      {requests.filter((r) => r.requested_by === "employee").length > 0 && (
        <div className="mt-6">
          <p className="text-sm font-semibold text-charcoal mb-2">Mis solicitudes</p>
          {requests.filter((r) => r.requested_by === "employee").map((r) => (
            <div key={r.id} data-testid={EMPLOYEE_VACATION_REQUEST.requestRow} className="bg-card border border-petroleum/10 rounded-2xl p-4 mb-2">
              <div className="flex justify-between">
                <p className="text-sm text-charcoal">{r.start_date} → {r.end_date} ({r.days_requested} días)</p>
                <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full shrink-0">{STATUS_LABEL[r.status] || r.status}</span>
              </div>
              {r.status === "rejected" && r.reviewed_reason && (
                <p className="text-xs text-red-600/80 mt-1">Motivo: {r.reviewed_reason}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </EmployeeLayout>
  );
}
